import React, { useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mountain, FileText, Trophy, Grip, CalendarDays, Shield, Settings, CreditCard, Users, User, Bell, Snowflake, Award } from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { MobileNav } from '../components/MobileNav';
import { TopBar } from '../components/TopBar';
import { MembershipCard } from '../components/MembershipCard';
import { PaymentCard } from '../components/PaymentCard';
import { ClubCard } from '../components/ClubCard';
import { ProfileCard } from '../components/ProfileCard';
import { NotificationsPanel } from '../components/NotificationsPanel';
import { PaymentsPage } from '../components/PaymentsPage';
import { CompetitionsPage } from '../components/CompetitionsPage';
import { MembershipOnboarding } from '../components/MembershipOnboarding';
// Define tabs configuration
const tabs = [{
  id: 'overview',
  label: 'Головна',
  icon: Mountain
}, {
  id: 'profile',
  label: 'Профіль',
  icon: User
}, {
  id: 'competitions',
  label: 'Події',
  icon: CalendarDays
}, {
  id: 'payments',
  label: 'Платежі',
  icon: CreditCard
}, {
  id: 'notifications',
  label: 'Сповіщення',
  icon: Bell
}, {
  id: 'settings',
  label: 'Налаштування',
  icon: Settings
}];
// Animation variants
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
export function PersonalCabinet() {
  const [activeTab, setActiveTab] = useState('overview');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const activeTabLabel = tabs.find(t => t.id === activeTab)?.label || 'Головна';
  return <div className="flex min-h-screen bg-slate-50 font-sans text-slate-900" data-id="element-612">
      {/* Sidebar Navigation (Desktop + Mobile overlay) */}
      <Sidebar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} isMobileOpen={isMobileSidebarOpen} onClose={() => setIsMobileSidebarOpen(false)} data-id="element-613" />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0" data-id="element-614">
        {/* Top Header */}
        <TopBar title={activeTabLabel} onMenuToggle={() => setIsMobileSidebarOpen(true)} data-id="element-615" />

        {/* Scrollable Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-24 md:pb-8 overflow-y-auto" data-id="element-616">
          <div className="max-w-6xl mx-auto" data-id="element-617">
            <AnimatePresence mode="wait" data-id="element-618">
              {activeTab === 'overview' ? <motion.div key="overview" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.3
            }} data-id="element-619">
                  {/* Welcome Section */}
                  <div className="mb-8" data-id="element-620">
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-id="element-621">
                      Вітаємо, Миколо! 👋
                    </h1>
                    <p className="text-slate-500 max-w-2xl" data-id="element-622">
                      Ваш статус членства неактивний, оформіть членство.
                    </p>
                  </div>

                  {/* Onboarding Widget */}
                  <motion.div variants={cardVariants} className="mb-6" data-id="element-623">
                    <MembershipOnboarding onNavigateToProfile={() => setActiveTab('profile')} data-id="element-624" />
                  </motion.div>

                  {/* Dashboard Grid */}
                  <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-625">
                    {/* Membership Status */}
                    <motion.div variants={cardVariants} className="h-full" data-id="element-626">
                      <MembershipCard data-id="element-627" />
                    </motion.div>

                    {/* Club Info */}
                    <motion.div variants={cardVariants} className="h-full" data-id="element-628">
                      <ClubCard data-id="element-629" />
                    </motion.div>

                    {/* Next Payment */}
                    <motion.div variants={cardVariants} className="h-full" data-id="element-630">
                      <PaymentCard data-id="element-631" />
                    </motion.div>

                    {/* Achievements / Stats */}
                    <motion.div variants={cardVariants} className="h-full md:col-span-2 xl:col-span-3" data-id="element-632">
                      <AchievementsCard data-id="element-633" />
                    </motion.div>
                  </motion.div>
                </motion.div> : activeTab === 'profile' ? <motion.div key="profile" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.3
            }} data-id="element-634">
                  <ProfileCard data-id="element-635" />
                </motion.div> : activeTab === 'notifications' ? <motion.div key="notifications" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.3
            }} data-id="element-636">
                  <NotificationsPanel data-id="element-637" />
                </motion.div> : activeTab === 'payments' ? <motion.div key="payments" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.3
            }} data-id="element-638">
                  <PaymentsPage data-id="element-639" />
                </motion.div> : activeTab === 'competitions' ? <motion.div key="competitions" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.3
            }} data-id="element-640">
                  <CompetitionsPage data-id="element-641" />
                </motion.div> : <EmptyState tabLabel={activeTabLabel} data-id="element-642" />}
            </AnimatePresence>
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileNav tabs={tabs.slice(0, 5)} // Show first 5 items on mobile to fit
    activeTab={activeTab} onTabChange={setActiveTab} data-id="element-643" />
    </div>;
}
// Helper Components
function EmptyState({
  tabLabel
}: {
  tabLabel: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} className="flex flex-col items-center justify-center py-32 text-center bg-white rounded-3xl border border-slate-100 shadow-sm" data-id="element-644">
      <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-6" data-id="element-645">
        <Settings className="w-10 h-10 text-slate-300" data-id="element-646" />
      </div>
      <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-647">
        Розділ "{tabLabel}"
      </h2>
      <p className="text-slate-500 max-w-md mx-auto" data-id="element-648">
        Цей функціонал знаходиться в розробці. Ми працюємо над тим, щоб зробити
        його доступним якомога швидше.
      </p>
    </motion.div>;
}
function AchievementsCard() {
  const SPORT_RANKS = ['Без розряду', 'III розряд', 'II розряд', 'I розряд', 'КМС', 'МС', 'МСМК', 'ЗМС'];
  const disciplines = [{
    label: 'Альпінізм',
    rank: 'КМС',
    icon: Mountain,
    color: '#3b8fd4',
    bg: '#eff8ff',
    border: '#bfdbf7',
    trackColor: '#3b8fd4'
  }, {
    label: 'Скелелазіння',
    rank: 'I розряд',
    icon: Grip,
    color: '#16a34a',
    bg: '#f0fdf4',
    border: '#bbf7d0',
    trackColor: '#16a34a'
  }, {
    label: 'Льодолазіння',
    rank: 'II розряд',
    icon: Snowflake,
    color: '#0891b2',
    bg: '#ecfeff',
    border: '#a5f3fc',
    trackColor: '#0891b2'
  }];
  const highestRankIndex = Math.max(...disciplines.map(d => SPORT_RANKS.indexOf(d.rank)));
  const highestRank = SPORT_RANKS[highestRankIndex];
  return <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-slate-100 h-full" data-id="element-649">
      <div className="flex items-center justify-between mb-6" data-id="element-650">
        <div data-id="element-651">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-652">
            <Award className="w-5 h-5 text-slate-400" data-id="element-653" />
            Спортивна кваліфікація
          </h3>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-654">
            Розряди за видами спорту
          </p>
        </div>
        <div className="px-3 py-1 rounded-full bg-yellow-50 text-yellow-700 text-xs font-bold border border-yellow-100" data-id="element-655">
          Найвищий: {highestRank}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-id="element-656">
        {disciplines.map(d => {
        const Icon = d.icon;
        const rankIndex = SPORT_RANKS.indexOf(d.rank);
        const progress = rankIndex / (SPORT_RANKS.length - 1);
        const nextRank = rankIndex < SPORT_RANKS.length - 1 ? SPORT_RANKS[rankIndex + 1] : null;
        return <div key={d.label} className="rounded-xl p-5 border flex flex-col gap-4" style={{
          backgroundColor: d.bg,
          borderColor: d.border
        }} data-id="element-657">
              {/* Header */}
              <div className="flex items-center gap-3" data-id="element-658">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{
              backgroundColor: 'white',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
            }} data-id="element-659">
                  <Icon className="w-4.5 h-4.5" style={{
                color: d.color
              }} data-id="element-660" />
                </div>
                <div data-id="element-661">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-662">
                    {d.label}
                  </p>
                  <p className="text-base font-bold" style={{
                color: d.color
              }} data-id="element-663">
                    {d.rank}
                  </p>
                </div>
              </div>

              {/* Progress bar */}
              <div data-id="element-664">
                <div className="flex justify-between items-center mb-1.5" data-id="element-665">
                  <span className="text-[10px] text-slate-400 font-medium" data-id="element-666">
                    Без розряду
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium" data-id="element-667">
                    ЗМС
                  </span>
                </div>
                <div className="h-2 rounded-full bg-white/70 overflow-hidden" style={{
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)'
            }} data-id="element-668">
                  <div className="h-full rounded-full transition-all duration-700" style={{
                width: `${progress * 100}%`,
                backgroundColor: d.trackColor
              }} data-id="element-669" />
                </div>
                <div className="flex justify-between mt-1.5" data-id="element-670">
                  <span className="text-[10px]" style={{
                color: d.color
              }} data-id="element-671">
                    {rankIndex + 1} з {SPORT_RANKS.length} рівнів
                  </span>
                  {nextRank && <span className="text-[10px] text-slate-400" data-id="element-672">
                      Наступний: {nextRank}
                    </span>}
                </div>
              </div>

              {/* Rank steps */}
              <div className="flex flex-wrap gap-1" data-id="element-673">
                {SPORT_RANKS.map((r, i) => <span key={r} className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={i <= rankIndex ? {
              backgroundColor: d.trackColor,
              color: 'white'
            } : {
              backgroundColor: 'white',
              color: '#94a3b8'
            }} data-id="element-674">
                    {r}
                  </span>)}
              </div>
            </div>;
      })}
      </div>
    </div>;
}